import type { ReactNode } from 'react';
import { APP } from '../tokens';

interface MobileBottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  collapsedLabel?: string;
  showCollapsedButton?: boolean;
  disabled?: boolean;
  children: ReactNode;
}

export default function MobileBottomSheet({
  open,
  onOpenChange,
  title,
  collapsedLabel = 'Ask Corner',
  showCollapsedButton = true,
  disabled = false,
  children,
}: MobileBottomSheetProps) {
  if (disabled) return <>{children}</>;

  return (
    <>
      {!open && showCollapsedButton && (
        <button
          type="button"
          onClick={() => onOpenChange(true)}
          style={{
            position: 'fixed',
            right: 14,
            bottom: 'calc(14px + env(safe-area-inset-bottom))',
            zIndex: 9000,
            minHeight: 44,
            padding: '0 16px',
            borderRadius: 999,
            border: `1px solid ${APP.border}`,
            background: APP.bgChatSendBtn,
            color: '#FDFCFB',
            fontFamily: APP.fontFamily,
            fontSize: 13,
            fontWeight: 700,
            boxShadow: '0 14px 34px rgba(42, 31, 24, 0.22)',
          }}
        >
          {collapsedLabel}
        </button>
      )}
      <div
        aria-hidden={!open}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9500,
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        <button
          type="button"
          aria-label="Close chat"
          onClick={() => onOpenChange(false)}
          style={{
            position: 'absolute',
            inset: 0,
            border: 'none',
            background: 'rgba(22, 17, 13, 0.28)',
            opacity: open ? 1 : 0,
            transition: 'opacity 180ms ease',
          }}
        />
        <section
          role="dialog"
          aria-modal="true"
          aria-label={title}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 'min(74dvh, 680px)',
            display: 'flex',
            flexDirection: 'column',
            borderTopLeftRadius: 18,
            borderTopRightRadius: 18,
            overflow: 'hidden',
            background: APP.bgChatPanel,
            boxShadow: '0 -18px 42px rgba(42, 31, 24, 0.25)',
            transform: open ? 'translateY(0)' : 'translateY(105%)',
            transition: 'transform 220ms ease',
          }}
        >
          <div
            style={{
              height: 46,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 14px',
              borderBottom: `1px solid ${APP.border}`,
              background: APP.bgContent,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span style={{ fontFamily: APP.fontFamily, fontSize: 13, fontWeight: 700, color: APP.textPrimary }}>
                {title}
              </span>
              <span style={{ fontFamily: APP.fontFamily, fontSize: 10, color: APP.textSecondary }}>
                Pull up to keep working with Corner
              </span>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Collapse chat"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                border: `1px solid ${APP.border}`,
                background: APP.bgVersionCard,
                color: APP.textPrimary,
                fontSize: 18,
                lineHeight: '18px',
              }}
            >
              ×
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
            {children}
          </div>
        </section>
      </div>
    </>
  );
}
